import React from "react";
import type { ActivityViewData, AsyncTaskMeta } from "../types";
import type { ActivityAction, ActivityItemOptions } from "../ui/types";
import { ActivityStatus } from "../constants";
import { isActiveStatus } from "../helpers";
import { ActivityItemShell } from "../ui/ActivityItemShell";
import { Chip } from "../../Chip";

declare const window: {
    page?: (url: string) => void;
    open?: (url: string, target: string) => void;
};

function getProgressPercent(progress: ActivityViewData["progress"]): number | null {
    if (!progress || !progress.total) return null;
    return Math.round((progress.current / progress.total) * 100);
}

const STATUS_CHIP_COLOR: Partial<Record<ActivityStatus, "gray" | "purple">> = {
    [ActivityStatus.Pending]: "gray",
    [ActivityStatus.Running]: "purple"
};

function getClickHandler(activity: ActivityViewData): (() => void) | undefined {
    if (!activity.targetUrl || activity.status !== ActivityStatus.Completed) return undefined;
    return () => {
        if (activity.openInNewTab && typeof window.open === "function") {
            window.open(activity.targetUrl!, "_blank");
        } else if (typeof window.page === "function") {
            window.page(activity.targetUrl!);
        }
    };
}

function RunningAsyncTask({
    activity,
    itemOptions
}: {
    activity: ActivityViewData;
    itemOptions?: ActivityItemOptions;
}) {
    const compact = itemOptions?.variant === "compact";
    const percent = getProgressPercent(activity.progress);

    const statusChip = (
        <Chip
            color={(activity.status && STATUS_CHIP_COLOR[activity.status]) ?? "gray"}
            className="flex-shrink-0 text-xs"
        >
            {activity.status === ActivityStatus.Running && (
                <i className="far fa-spinner-third fa-spin mr-1" aria-hidden />
            )}
            {activity.statusLabel ??
                (activity.status === ActivityStatus.Running ? "Running" : "Pending")}
        </Chip>
    );

    return (
        <ActivityItemShell
            contextLabel={activity.contextLabel}
            title={activity.title}
            icon={activity.icon}
            showUnreadDot={compact ? false : !!itemOptions?.onMarkRead && !activity.read}
            timestamp={compact ? undefined : activity.createdAt}
            trailing={statusChip}
            onMarkRead={compact ? undefined : itemOptions?.onMarkRead}
            action={itemOptions?.action}
        >
            {activity.description && (
                <span title={activity.description}>{activity.description}</span>
            )}
            {activity.status === ActivityStatus.Running &&
                percent !== null &&
                activity.progress && (
                    <div className="mt-2 overflow-visible whitespace-normal">
                        <div className="flex items-center justify-between">
                            <span>
                                {activity.progress.current} / {activity.progress.total}
                            </span>
                            <span>{percent}%</span>
                        </div>
                        <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                            <div
                                className="h-full rounded-full bg-brand transition-all"
                                style={{ width: `${percent}%` }}
                            />
                        </div>
                    </div>
                )}
        </ActivityItemShell>
    );
}

function CompletedAsyncTask({
    activity,
    itemOptions
}: {
    activity: ActivityViewData;
    itemOptions?: ActivityItemOptions;
}) {
    const compact = itemOptions?.variant === "compact";
    const isFailed = activity.status === ActivityStatus.Failed;
    const metaAction = (activity.meta?.action as ActivityAction) ?? undefined;
    const hasMetaAction = metaAction != null;
    const onClick = hasMetaAction ? undefined : getClickHandler(activity);

    return (
        <ActivityItemShell
            contextLabel={activity.contextLabel}
            title={activity.title}
            icon={activity.icon}
            showUnreadDot={compact ? false : !!itemOptions?.onMarkRead && !activity.read}
            timestamp={compact ? undefined : activity.createdAt}
            secondary={isFailed ? <span className="text-red-500">failed</span> : undefined}
            onClick={onClick}
            onMarkRead={compact ? undefined : itemOptions?.onMarkRead}
            action={hasMetaAction ? metaAction : itemOptions?.action}
            alwaysShowActions={hasMetaAction}
        >
            {activity.description && (
                <span title={activity.description}>{activity.description}</span>
            )}
            {(activity.meta as AsyncTaskMeta)?.completionNote &&
                activity.status === ActivityStatus.Completed && (
                    <span className="text-gray-400">
                        {(activity.meta as AsyncTaskMeta).completionNote}
                    </span>
                )}
            {activity.error && (
                <span className="text-red-500" title={activity.error}>
                    {activity.error}
                </span>
            )}
        </ActivityItemShell>
    );
}

/** Renders an async task activity. Routes to `RunningAsyncTask` (progress, status chip) or `CompletedAsyncTask` (actions, error, click-to-navigate) based on status. */
export function AsyncTaskItem({
    activity,
    itemOptions
}: {
    activity: ActivityViewData;
    itemOptions?: ActivityItemOptions;
}) {
    if (isActiveStatus(activity.status)) {
        return <RunningAsyncTask activity={activity} itemOptions={itemOptions} />;
    }
    return <CompletedAsyncTask activity={activity} itemOptions={itemOptions} />;
}
